/**
 * Dialog de configuração do node Google Tag Manager
 * Permite configurar nome do evento e variáveis key/value
 * 
 * RELEASE: 1.1.0 - 2026-03-08 - Adicionado botão de ajuda contextual (NodeHelpDialog)
 * RELEASE: 1.0.0 - 2026-03-08 - Novo dialog de rastreamento GTM
 */

import { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Tags, HelpCircle } from 'lucide-react';
import { FlowNode } from '@/types/flows';
import { NodeIdField, NodeIdFieldRef } from '@/components/tenant/flows/NodeIdField';
import { NodeHelpDialog } from './NodeHelpDialog';

interface ConfigureGTMDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: Record<string, any>) => void;
}

/**
 * Dialog para configurar evento GTM com variáveis dinâmicas
 */
export function ConfigureGTMDialog({ open, onOpenChange, node, onSave }: ConfigureGTMDialogProps) {
  const config = node.data.config || {};
  const [eventName, setEventName] = useState<string>(config.event_name || '');
  const [variables, setVariables] = useState<Array<{ key: string; value: string }>>(
    Object.entries((config.variables as Record<string, string>) || {}).map(([key, value]) => ({ key, value }))
  );
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  /** Controle do modal de ajuda */
  const [showHelp, setShowHelp] = useState(false);

  /** Adiciona par chave/valor */
  const addVariable = () => {
    setVariables(prev => [...prev, { key: '', value: '' }]);
  };

  /** Remove par chave/valor pelo índice */
  const removeVariable = (index: number) => {
    setVariables(prev => prev.filter((_, i) => i !== index));
  };

  /** Atualiza chave ou valor de um par */
  const updateVariable = (index: number, field: 'key' | 'value', val: string) => {
    setVariables(prev => prev.map((v, i) => i === index ? { ...v, [field]: val } : v));
  };

  /** Salva configuração do node */
  const handleSave = () => {
    const varsRecord: Record<string, string> = {};
    variables.forEach(v => {
      if (v.key.trim()) varsRecord[v.key.trim()] = v.value;
    });

    onSave(node.id, {
      ...config,
      event_name: eventName,
      variables: varsRecord,
      nickname: nicknameRef.current?.getNickname(),
    });
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Tags className="h-5 w-5 text-blue-600" />
              Google Tag Manager
            </DialogTitle>
            <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
              <HelpCircle className="h-5 w-5" />
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* ID do Node + Apelido */}
          <NodeIdField
            ref={nicknameRef}
            nodeId={node.id}
            defaultNickname={config.nickname}
          />

          {/* Nome do Evento */}
          <div className="space-y-2">
            <Label>Nome do Evento *</Label>
            <Input
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="generate_lead"
            />
            <p className="text-xs text-muted-foreground">
              Evento disparado no <code>dataLayer.push()</code>
            </p>
          </div>

          {/* Variáveis */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Variáveis (opcional)</Label>
              <Button variant="outline" size="sm" onClick={addVariable}>
                <Plus className="h-3 w-3 mr-1" /> Adicionar
              </Button>
            </div>

            {variables.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={v.key}
                  onChange={(e) => updateVariable(i, 'key', e.target.value)}
                  placeholder="chave"
                  className="flex-1 h-8 text-sm"
                />
                <Input
                  value={v.value}
                  onChange={(e) => updateVariable(i, 'value', e.target.value)}
                  placeholder="valor ou {{variavel}}"
                  className="flex-1 h-8 text-sm"
                />
                <Button variant="ghost" size="sm" onClick={() => removeVariable(i)} className="h-8 px-2">
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            ))}

            {variables.length === 0 && (
              <p className="text-xs text-muted-foreground italic">
                Nenhuma variável adicionada
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!eventName.trim()}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="tracking_gtm" />
    </>
  );
}
